import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

interface CallbackSearch {
  redirect?: string | undefined;
}

// Extracted so it can be unit-tested without mounting the route component.
// GoTrue reports OAuth failures (e.g. denied consent, provider errors) by redirecting
// back with `error`/`error_description` in the hash or query string rather than
// throwing from getSession(), so we have to check for them explicitly.
export function parseAuthErrorFromLocation(hash: string, search: string): string | null {
  const hashParams = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const searchParams = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);

  const error = hashParams.get("error") || searchParams.get("error");
  if (!error) return null;

  // URLSearchParams already percent-decodes and converts "+" to spaces
  const description = hashParams.get("error_description") || searchParams.get("error_description");
  return description || "Sign in failed. Please try again.";
}

// Extracted so the getSession decision can be unit-tested in isolation.
// After detectSessionInUrl performs the token/code exchange, getSession() can
// return an error, a missing session (silent exchange failure), or a session.
export function resolveSessionResult(
  data: { session: unknown | null },
  error: { message: string } | null
): { status: "success" } | { status: "error"; message: string } | { status: "no-session"; message: string } {
  if (error) {
    return { status: "error", message: error.message };
  }
  if (!data.session) {
    return { status: "no-session", message: "Sign in didn't complete. Please try again." };
  }
  return { status: "success" };
}

export const Route = createFileRoute("/auth/callback")({
  validateSearch: (search: Record<string, unknown>): CallbackSearch => {
    return {
      redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    };
  },
  component: AuthCallback,
});

function AuthCallback() {
  const navigate = useNavigate();
  const { redirect: redirectPath } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      // GoTrue can redirect back with an error in the URL instead of raising one
      // from getSession() - check for that first.
      const urlError = parseAuthErrorFromLocation(window.location.hash, window.location.search);
      if (urlError) {
        console.error("Auth callback error:", urlError);
        setError(urlError);
        setTimeout(() => {
          navigate({ to: "/login", search: { error: "auth_failed" } });
        }, 2000);
        return;
      }

      // Supabase automatically handles the token exchange from the URL hash/code
      // when detectSessionInUrl is true (configured in supabase.ts)
      const { data, error } = await supabase.auth.getSession();

      const result = resolveSessionResult(data, error);
      switch (result.status) {
        case "error":
          console.error("Auth callback error:", error);
          setError(result.message);
          // Redirect to login with error after a short delay
          setTimeout(() => {
            navigate({ to: "/login", search: { error: "auth_failed" } });
          }, 2000);
          break;
        case "no-session":
          // No error thrown, but also no session - the exchange silently failed.
          console.error("Auth callback: no session after token exchange");
          setError(result.message);
          setTimeout(() => {
            navigate({ to: "/login", search: { error: "auth_failed" } });
          }, 2000);
          break;
        case "success":
          // Success - redirect to intended destination or clubs
          navigate({ to: redirectPath || "/clubs" });
          break;
      }
    };

    handleCallback();
  }, [navigate, redirectPath]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-4">
        <div className="text-center space-y-4">
          <div className="text-accent-negative text-lg font-medium">
            Sign in failed
          </div>
          <p className="text-text-secondary text-sm">{error}</p>
          <p className="text-text-tertiary text-xs">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4">
      <div className="text-center space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent-primary mx-auto" />
        <p className="text-text-secondary">Completing sign in...</p>
      </div>
    </div>
  );
}
